import { t } from '../../../i18n';
import { buttonStyle, createSection } from './common';
import type { PlayerLookupHandler } from '../types';

interface PlayerUtilitiesState {
  playerQuery: string;
  searchStatus: string | null;
  isSearching: boolean;
  isDisconnecting: boolean;
  isBringing: boolean;
}

interface PlayerUtilitiesActions {
  setPlayerQuery: (value: string) => void;
  setSearchStatus: (value: string | null) => void;
  setSearching: (value: boolean) => void;
  setDisconnecting: (value: boolean) => void;
  setBringing: (value: boolean) => void;
  render: () => void;
  scanPlayersOnMap: PlayerLookupHandler;
  disconnectPlayerSession: PlayerLookupHandler;
  bringPlayerHere: PlayerLookupHandler;
}

export function renderPlayerUtilitiesSection(
  state: PlayerUtilitiesState,
  actions: PlayerUtilitiesActions
): HTMLDivElement {
  const section = createSection('admin.tabs.players');

  const desc = document.createElement('p');
  desc.textContent = t('admin.playersDesc');
  Object.assign(desc.style, { color: '#cbd5e1', margin: '0' });
  section.appendChild(desc);

  const form = document.createElement('form');
  Object.assign(form.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  });

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = t('admin.playerPlaceholder');
  input.value = state.playerQuery;
  Object.assign(input.style, {
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid rgba(148, 163, 184, 0.3)',
    background: 'rgba(15, 23, 42, 0.75)',
    color: '#f8fafc',
  });
  input.addEventListener('input', (event) => {
    actions.setPlayerQuery((event.target as HTMLInputElement).value);
  });
  input.addEventListener('keydown', (event) => event.stopPropagation());
  form.appendChild(input);

  const findBtn = document.createElement('button');
  findBtn.type = 'submit';
  findBtn.textContent = t('admin.findPlayer');
  Object.assign(findBtn.style, buttonStyle('#1d4ed8'));

  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    gap: '8px',
  });

  const disconnectBtn = document.createElement('button');
  disconnectBtn.type = 'button';
  disconnectBtn.textContent = t('admin.disconnectSession');
  Object.assign(disconnectBtn.style, buttonStyle('#b91c1c'));

  const bringBtn = document.createElement('button');
  bringBtn.type = 'button';
  bringBtn.textContent = t('admin.bringPlayerHere');
  Object.assign(bringBtn.style, buttonStyle('#15803d'));

  row.appendChild(disconnectBtn);
  row.appendChild(bringBtn);
  form.appendChild(findBtn);
  form.appendChild(row);
  section.appendChild(form);

  const status = document.createElement('div');
  Object.assign(status.style, {
    color: '#f8fafc',
    fontSize: '12px',
    padding: '8px',
    borderRadius: '6px',
    background: 'rgba(15, 23, 42, 0.75)',
    display: state.isSearching || state.isDisconnecting || state.isBringing || state.searchStatus ? 'block' : 'none',
  });
  status.textContent = state.isSearching
    ? t('admin.status.scanning')
    : state.isDisconnecting
      ? t('admin.status.disconnecting')
      : state.isBringing
        ? t('admin.status.teleporting')
        : state.searchStatus ?? '';
  section.appendChild(status);

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const query = state.playerQuery.trim();
    if (!query) {
      actions.setSearchStatus(t('admin.errors.enterSearch'));
      actions.render();
      return;
    }
    try {
      actions.setSearching(true);
      actions.render();
      const result = await actions.scanPlayersOnMap(query);
      actions.setSearchStatus(result.message);
    } catch (error) {
      console.error('Admin player scan failed', error);
      actions.setSearchStatus(t('admin.errors.scanFailed'));
    } finally {
      actions.setSearching(false);
      actions.render();
    }
  });

  disconnectBtn.addEventListener('click', async () => {
    const query = state.playerQuery.trim();
    if (!query) {
      actions.setSearchStatus(t('admin.errors.enterDisconnect'));
      actions.render();
      return;
    }
    try {
      actions.setDisconnecting(true);
      actions.render();
      const result = await actions.disconnectPlayerSession(query);
      actions.setSearchStatus(result.message);
    } catch (error) {
      console.error('Admin disconnect failed', error);
      actions.setSearchStatus(t('admin.errors.disconnectFailed'));
    } finally {
      actions.setDisconnecting(false);
      actions.render();
    }
  });

  bringBtn.addEventListener('click', async () => {
    const query = state.playerQuery.trim();
    if (!query) {
      actions.setSearchStatus(t('admin.errors.enterTeleport'));
      actions.render();
      return;
    }
    try {
      actions.setBringing(true);
      actions.render();
      const result = await actions.bringPlayerHere(query);
      actions.setSearchStatus(result.message);
    } catch (error) {
      console.error('Admin bring player failed', error);
      actions.setSearchStatus(t('admin.errors.bringFailed'));
    } finally {
      actions.setBringing(false);
      actions.render();
    }
  });

  return section;
}
